import type { ClientDto } from '@fos/shared/contracts/crm';
import { ChevronRight, FolderOpen, Lock } from 'lucide-react';
import { Link } from 'react-router';
import { appErrorCode } from '../../../../lib/api';
import { trpc } from '../../../../lib/trpc';
import { useAccess } from '../../../../lib/useAccess';
import { Section } from '../../../../ui/Section';
import { EmptyState, ErrorState, Skeleton } from '../../../../ui/States';
import { AgreementsSection } from './AgreementsSection';
import { DocumentFilesSection } from './DocumentFilesSection';
import { DocumentLinksSection } from './DocumentLinksSection';

function DocumentsSkeleton() {
  return (
    <div role="status" aria-label="Wczytywanie dokumentów" className="flex flex-col gap-4 md:gap-6">
      <Skeleton className="h-44 w-full rounded-xl" />
      <Skeleton className="h-28 w-full rounded-xl" />
      <Skeleton className="h-28 w-full rounded-xl" />
    </div>
  );
}

/**
 * Zakładka „Dokumenty” profilu klienta (DECISIONS 2026-10-17 [P] „Dokumenty klienta: pliki, linki, ustalenia”): jedno
 * miejsce na umowę, oferty i materiały klienta (Pliki), linki klienta (Linki) i warunki współpracy (Ustalenia: usługi
 * i decyzje z Wiedzy). Dane z `clients.documents.list` — każda grupa z uprawnieniami swojego modułu; osoba z dostępem
 * tylko przez projekt widzi wyłącznie pliki projektu (bez linków).
 */
export function ClientDocumentsTab({ client }: { client: ClientDto }) {
  const access = useAccess();
  const enabled = access.can('clients');
  const query = trpc.clients.documents.list.useQuery({ clientId: client.id }, { enabled });
  if (!enabled) {
    return (
      <Section>
        <EmptyState icon={Lock} title="Brak dostępu do modułu Klienci" />
      </Section>
    );
  }
  if (query.isPending) return <DocumentsSkeleton />;
  if (query.isError) {
    return (
      <Section>
        {appErrorCode(query.error) === 'NOT_FOUND' ? (
          <EmptyState icon={Lock} title="Brak dostępu do dokumentów tego klienta" />
        ) : (
          <ErrorState onRetry={() => void query.refetch()} retrying={query.isFetching} />
        )}
      </Section>
    );
  }
  const data = query.data;
  const archived = client.status === 'archived' || client.archivedAt !== null;
  const showAgreements = data.agreements.services !== null || data.agreements.decisions !== null;
  return (
    <div className="flex flex-col gap-4 md:gap-6">
      <DocumentFilesSection clientId={client.id} files={data.files} archived={archived} />
      {data.links && <DocumentLinksSection clientId={client.id} links={data.links} />}
      {showAgreements && <AgreementsSection clientId={client.id} agreements={data.agreements} />}
    </div>
  );
}

/**
 * Wejście do „Dokumentów” na Przeglądzie klienta — tylko telefon (zakładki profilu są tam ukryte; desktop ma zakładkę).
 * Sekcja Przeglądu modułu klientów (`registerClientOverviewSection`, prawa kolumna).
 */
export function ClientDocumentsShortcut({ client }: { client: ClientDto }) {
  return (
    <Link
      to={`/klienci/${client.id}?zakladka=dokumenty`}
      className="flex min-h-14 items-center gap-3 rounded-xl border border-line bg-panel px-4 py-3 hover:bg-surface focus-visible:outline-2 focus-visible:outline-accent md:hidden"
    >
      <span
        aria-hidden
        className="inline-flex size-9 shrink-0 items-center justify-center rounded-md bg-surface text-fg-secondary"
      >
        <FolderOpen size={18} strokeWidth={1.75} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="text-body-s font-medium text-fg">Dokumenty</span>
        <span className="truncate text-caption text-fg-muted">Umowy, pliki, linki i ustalenia</span>
      </span>
      <ChevronRight size={18} strokeWidth={1.75} aria-hidden className="shrink-0 text-fg-muted" />
    </Link>
  );
}
